'use client';

import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from './auth-context';
import { useFirebase } from '@/firebase';
import { collection, query, onSnapshot, doc, updateDoc, deleteDoc, orderBy, limit, writeBatch, setDoc, getDoc } from 'firebase/firestore';
import {
    browserLocalStorage,
    readStorageItem,
    removeStorageItems,
    writeStorageItem,
} from '@/lib/browser-storage';

export type NotificationType =
    | 'app'
    | 'toast'
    | 'sale'
    | 'product'
    | 'product_deleted'
    | 'product_updated'
    | 'seller_deleted'
    | 'message'
    | 'discount_end'
    | 'discount_live'
    | 'purchase'
    | 'refund_result'
    | 'seller_verified'
    | 'tier_change'
    | 'review'
    | 'review_response'
    | 'refund_request'
    | 'subscription_started'
    | 'subscription_cancelled'
    | 'subscription_ended'
    // Recurring-billing lifecycle: renewal succeeded/failed, dunning cancel, and
    // Diamond price changes. Fired for both Diamond and product subscriptions.
    | 'subscription'
    // A product was rejected/removed by server-side enforcement (e.g. storage cap).
    | 'product_rejected'
    // A seller's product went live for the first time (draft to live). Links to
    // its payment link; see functions/src/lib/product-launch-notice.ts.
    | 'product_live'
    // Identity/business verification outcome: approved, or resubmit needed.
    | 'seller_verification'
    // A declined check put the seller on an identity hold: listings archived,
    // payouts paused until a retry passes. No off switch (see ALWAYS_ON_EVENTS
    // on the notification settings page): it means their products left sale.
    | 'identity_hold'
    // Payout (withdrawal to bank) sent / failed-and-reversed.
    | 'payout_paid'
    | 'payout_failed'
    // Bank account connected for the first time. Fired once per seller.
    | 'payouts_ready'
    // A buyer's bank reversed a settled payment. Distinct from 'refund_request':
    // there is nothing for the seller to approve or contest here, the money has
    // already been pulled and deducted from their balance.
    | 'chargeback'
    | 'guest_purchase_migrated'
    // A buyer stopped sharing their email with this creator (lib/email-share).
    // No off switch: the creator has 5 business days to remove the address from
    // their own mailing tool (see ALWAYS_ON_EVENTS on the settings page).
    | 'email_share_stopped'
    | 'username_assigned'
    | 'account_security'
    // The seller finished the setup checklist (completeSellerSetup). Once per account.
    | 'seller_setup_complete';

export type AppNotification = {
  id: string;
  type: NotificationType;
  title: string;
  description: string;
  read: boolean;
  seen: boolean;
  href?: string;
  createdAt: Date;
  from?: string; // For grouping messages
  product?: {
    name: string;
    price?: number;
  },
};

// `addNotification` is client-side only — persistent notifications are
// emitted server-side via notifyUser (see functions/src/index.ts), and
// Firestore rules now forbid client create on the notifications subcollection.
// The payload type is narrowed to the ephemeral types so a future caller
// can't accidentally attempt a persistent write that would silently fail.
type NotificationPayload =
    | { type: 'toast', title: string, description?: string, href?: string }
    | { type: 'app' | 'product', title?: string, description?: string, href?: string }

type AddNotificationOptions = {
    silent?: boolean;
}

export type NotificationSettings = {
    [key in NotificationType]: boolean;
}

// No refundResult: refund decision emails are always sent (they concern the
// user's money), so an `email.refundResult` left in an older stored doc is
// ignored everywhere, here and in functions/src/lib/email-prefs.ts.
export type EmailNotificationPrefs = {
    announcements: boolean;
    adminActions: boolean;
    /** The review-request email after a download (functions/src/lib/review-requests.ts). */
    reviewRequests: boolean;
    /** The seller setup reminders (functions/src/lib/onboarding-reminders.ts). */
    setupReminders: boolean;
}

/**
 * How many notification documents the listener holds, and how many survive the
 * per-type filter to reach the panel.
 *
 * The read window is deliberately the wider of the two: the toggles are applied
 * client-side (Firestore cannot filter on a set the user is still editing), so
 * anything muted has to be read before it can be dropped. The gap between the
 * two is the headroom that keeps the panel full for someone who has switched a
 * noisy type off.
 */
const NOTIFICATION_READ_LIMIT = 200;
const NOTIFICATION_DISPLAY_LIMIT = 50;

/** Ephemeral types fire a toast and return early — they are never persisted to Firestore. */
const EPHEMERAL_TYPES: ReadonlySet<NotificationType> = new Set<NotificationType>([
    'toast',
    'app',
    'product',
]);

interface NotificationsContextType {
  notifications: AppNotification[];
  addNotification: (payload: NotificationPayload, options?: AddNotificationOptions) => void;
  removeNotification: (notificationId: string) => void;
  removeAllNotifications: () => Promise<void>;
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
  markAllAsSeen: () => void;
  markTypesAsRead: (types: NotificationType[]) => void;
  notificationSettings: NotificationSettings;
  setNotificationSetting: (type: NotificationType, enabled: boolean) => void;
  emailPrefs: EmailNotificationPrefs;
  setEmailPref: (key: keyof EmailNotificationPrefs, enabled: boolean) => Promise<void>;
  /**
   * The notifications listener failed. `notifications` is then empty because
   * nothing could be read, not because there is nothing to read, so the bell
   * says it could not load rather than "You're all caught up".
   */
  loadError: boolean;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

const defaultNotificationSettings: NotificationSettings = {
    app: true,
    toast: true,
    sale: true,
    product: true,
    product_deleted: true,
    product_updated: true,
    seller_deleted: true,
    message: true,
    discount_end: true,
    discount_live: true,
    purchase: true,
    refund_result: true,
    seller_verified: true,
    tier_change: true,
    review: true,
    review_response: true,
    refund_request: true,
    subscription_started: true,
    subscription_cancelled: true,
    subscription_ended: true,
    subscription: true,
    product_rejected: true,
    product_live: true,
    seller_verification: true,
    identity_hold: true,
    payout_paid: true,
    payout_failed: true,
    payouts_ready: true,
    chargeback: true,
    guest_purchase_migrated: true,
    email_share_stopped: true,
    username_assigned: true,
    account_security: true,
    seller_setup_complete: true,
};

const defaultEmailPrefs: EmailNotificationPrefs = {
    announcements: true,
    adminActions: true,
    reviewRequests: true,
    setupReminders: true,
};

type StoredNotificationsDoc = Partial<{
    types: Partial<NotificationSettings>;
    email: Partial<EmailNotificationPrefs>;
}> & Partial<NotificationSettings>; // legacy flat shape tolerated on read

// Accepts both the new `{ types, email }` shape and the legacy flat
// per-type-boolean shape. Returns normalised values merged with defaults.
function parseStoredDoc(data: StoredNotificationsDoc | undefined): {
    types: NotificationSettings;
    email: EmailNotificationPrefs;
} {
    if (!data) {
        return { types: defaultNotificationSettings, email: defaultEmailPrefs };
    }
    const hasNewShape = 'types' in data || 'email' in data;
    const legacyTypes = hasNewShape ? {} : (data as Partial<NotificationSettings>);
    return {
        types: { ...defaultNotificationSettings, ...(data.types ?? legacyTypes) },
        email: { ...defaultEmailPrefs, ...(data.email ?? {}) },
    };
}

export function NotificationsProvider({ children }: { children: ReactNode }) {
  // Raw docs from Firestore. The public `notifications` value is derived from
  // this by filtering out types the user has toggled off in settings so
  // server-inserted notifications honour the same per-type switches that
  // client-side `addNotification` already respects.
  const [rawNotifications, setRawNotifications] = useState<AppNotification[]>([]);
  const notificationsRef = useRef<AppNotification[]>([]);
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>(defaultNotificationSettings);
  const [emailPrefs, setEmailPrefs] = useState<EmailNotificationPrefs>(defaultEmailPrefs);
  const { toast } = useToast();
  const { authUser } = useAuth();
  const { firestore } = useFirebase();
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Load notification settings: prefer Firestore for authenticated users, fall
  // back to localStorage otherwise. Tolerates both the new `{ types, email }`
  // shape and the legacy flat per-type map.
  //
  // Storage goes through src/lib/browser-storage.ts. The reset in the catch
  // below used to call localStorage again, unguarded, from inside the handler
  // for the very failure that blocked storage produces, so a browser with site
  // data blocked rethrew out of this effect and every page showed the global
  // error screen. Blocked storage now just means the defaults.
  useEffect(() => {
    const loadFromLocalStorage = () => {
      const storage = browserLocalStorage();
      const stored = readStorageItem(storage, 'notificationSettings');
      if (!stored || stored === 'undefined') return;
      try {
        const parsed = parseStoredDoc(JSON.parse(stored));
        setNotificationSettings(parsed.types);
        setEmailPrefs(parsed.email);
      } catch (error) {
        console.error("Failed to load notification settings from localStorage. Resetting.", error);
        removeStorageItems(storage, 'notificationSettings');
      }
    };

    if (!firestore || !authUser) {
      loadFromLocalStorage();
      setSettingsLoaded(true);
      return;
    }

    const settingsRef = doc(firestore, 'users', authUser.uid, 'settings', 'notifications');
    getDoc(settingsRef).then((snap) => {
      if (snap.exists()) {
        const parsed = parseStoredDoc(snap.data() as StoredNotificationsDoc);
        setNotificationSettings(parsed.types);
        setEmailPrefs(parsed.email);
      } else {
        loadFromLocalStorage();
      }
      setSettingsLoaded(true);
    }).catch((error) => {
      console.error("Failed to load notification settings from Firestore.", error);
      loadFromLocalStorage();
      setSettingsLoaded(true);
    });
  }, [firestore, authUser]);

  useEffect(() => {
    if (!firestore || !authUser) {
        notificationsRef.current = [];
        setRawNotifications([]);
        setLoadError(false);
        setIsLoaded(true);
        return;
    }
    setLoadError(false);

    const notifsRef = collection(firestore, 'users', authUser.uid, 'notifications');
    const q = query(notifsRef, orderBy('createdAt', 'desc'), limit(NOTIFICATION_READ_LIMIT));

    const unsubscribe = onSnapshot(q, (snapshot) => {
        const firestoreNotifications = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
                id: doc.id,
                ...data,
                createdAt: data.createdAt?.toDate()
            } as AppNotification;
        });
        notificationsRef.current = firestoreNotifications;
        setRawNotifications(firestoreNotifications);
        setLoadError(false);
        setIsLoaded(true);
    }, (error) => {
        console.error("Error fetching notifications: ", error);
        setLoadError(true);
        setIsLoaded(true);
    });

    return () => unsubscribe();
  }, [firestore, authUser]);

  // Apply the per-type toggle, THEN cap. Unknown types (e.g. backend introduces
  // a new notification kind before the client is updated) fail open: shown
  // rather than hidden. Ephemeral types never reach Firestore so are not a
  // concern.
  //
  // The cap used to be the query's own `limit`, which put it on the wrong side
  // of this filter: muted notifications still occupied the window, so a user who
  // switched off a chatty type (messages, say) got a list that was mostly gone:
  // fifty documents read, a handful shown, and everything older silently out of
  // reach. Reading a wider window and capping the KEPT rows means the switches
  // change which notifications you see rather than how many.
  const notifications = useMemo(
      () => rawNotifications
          .filter(n => notificationSettings[n.type] !== false)
          .slice(0, NOTIFICATION_DISPLAY_LIMIT),
      [rawNotifications, notificationSettings],
  );

  // Cache the full settings blob locally so guests have stable prefs and so
  // the page renders immediately with the last-known state on reload.
  // Best-effort, like every write in this file.
  useEffect(() => {
    if (!settingsLoaded) return;
    const blob = { types: notificationSettings, email: emailPrefs };
    writeStorageItem(browserLocalStorage(), 'notificationSettings', JSON.stringify(blob));
  }, [notificationSettings, emailPrefs, settingsLoaded]);

  const persistToFirestore = useCallback(async (partial: {
    types?: Partial<NotificationSettings>;
    email?: Partial<EmailNotificationPrefs>;
  }) => {
    if (!firestore || !authUser) return;
    const ref = doc(firestore, 'users', authUser.uid, 'settings', 'notifications');
    await setDoc(ref, partial, { merge: true });
  }, [firestore, authUser]);

  const setNotificationSetting = (type: NotificationType, enabled: boolean) => {
    setNotificationSettings(prev => {
      const next = { ...prev, [type]: enabled };
      persistToFirestore({ types: { [type]: enabled } }).catch((error) => {
        console.error("Failed to save notification type to Firestore.", error);
      });
      return next;
    });
  }

  const setEmailPref = async (key: keyof EmailNotificationPrefs, enabled: boolean) => {
    setEmailPrefs(prev => ({ ...prev, [key]: enabled }));
    await persistToFirestore({ email: { [key]: enabled } });
  }

  const addNotification = useCallback((payload: NotificationPayload, options: AddNotificationOptions = {}) => {
    if (!notificationSettings[payload.type]) {
      return;
    }
    if (!options.silent) {
        toast({ title: payload.title, description: payload.description });
    }
  }, [toast, notificationSettings]);

  const removeNotification = useCallback(async (notificationId: string) => {
    if (authUser && firestore) {
        const notifRef = doc(firestore, 'users', authUser.uid, 'notifications', notificationId);
        await deleteDoc(notifRef);
    }
  }, [authUser, firestore]);

  const removeAllNotifications = useCallback(async () => {
    if (!authUser || !firestore) return;
    // Iterate the raw list rather than the filtered `notifications` view so a
    // type the user has toggled off in settings still gets cleared — leaving
    // those orphan rows would resurface them as unread the moment the type is
    // re-enabled. Same rationale as markAllAsSeen / markAllAsRead.
    const docs = notificationsRef.current;
    if (docs.length === 0) return;
    // Firestore batch caps at 500 ops; chunk so this stays correct however high
    // NOTIFICATION_READ_LIMIT goes. (The mark-as-read/seen batches below are
    // unchunked and therefore depend on that constant staying under 500.)
    const CHUNK = 400;
    for (let i = 0; i < docs.length; i += CHUNK) {
      const batch = writeBatch(firestore);
      for (const n of docs.slice(i, i + CHUNK)) {
        const notifRef = doc(firestore, 'users', authUser.uid, 'notifications', n.id);
        batch.delete(notifRef);
      }
      await batch.commit();
    }
  }, [authUser, firestore]);

  const markAsRead = useCallback(async (notificationId: string) => {
    if (authUser && firestore) {
        const notifRef = doc(firestore, 'users', authUser.uid, 'notifications', notificationId);
        await updateDoc(notifRef, { read: true });
    }
  }, [authUser, firestore]);

  const markAllAsRead = useCallback(async () => {
    if (!authUser || !firestore) return;
    // Iterate the raw list (not the filtered `notifications` view) so that
    // notifications of types the user has toggled off in settings still get
    // marked read — leaving them unread would resurface them as "unread" if
    // the user ever re-enables the type. Same rationale as markAllAsSeen.
    const batch = writeBatch(firestore);
    let dirty = false;
    notificationsRef.current.forEach((n) => {
      if (!n.read) {
        const notifRef = doc(firestore, 'users', authUser.uid, 'notifications', n.id);
        batch.update(notifRef, { read: true });
        dirty = true;
      }
    });
    if (dirty) await batch.commit();
  }, [authUser, firestore]);

  const markTypesAsRead = useCallback(async (types: NotificationType[]) => {
     if (authUser && firestore) {
        const batch = writeBatch(firestore);
        notificationsRef.current.forEach(n => {
            if (types.includes(n.type) && !n.read) {
                const notifRef = doc(firestore, 'users', authUser.uid, 'notifications', n.id);
                batch.update(notifRef, { read: true });
            }
        });
        await batch.commit();
     }
  }, [authUser, firestore]);

  const markAllAsSeen = useCallback(async () => {
    if (authUser && firestore) {
        // Iterate `notificationsRef.current` (raw) rather than the filtered
        // `notifications` view. If the user has disabled a type in settings,
        // those notifications don't render but they still exist in Firestore;
        // leaving them unseen means they resurface as "unread" if the user
        // ever re-enables the type. `markTypesAsRead` already uses the ref
        // for the same reason — this keeps both consistent.
        const batch = writeBatch(firestore);
        notificationsRef.current.forEach(n => {
            if (!n.seen) {
                const notifRef = doc(firestore, 'users', authUser.uid, 'notifications', n.id);
                batch.update(notifRef, { seen: true });
            }
        });
        await batch.commit();
    }
  }, [authUser, firestore]);


  const value = {
      notifications,
      addNotification,
      removeNotification,
      removeAllNotifications,
      markAsRead,
      markAllAsRead,
      markAllAsSeen,
      markTypesAsRead,
      notificationSettings,
      setNotificationSetting,
      emailPrefs,
      setEmailPref,
      loadError,
    };

  // Render children unconditionally — pages that consume notifications can
  // gate on `isLoaded` from context when they need to, but the public site
  // (product pages, marketing, checkout) doesn't depend on notifications
  // and shouldn't flash blank while the listener returns its first snapshot.
  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

export function useNotifications() {
  const context = useContext(NotificationsContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
}
